import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparisionUtils';
import {
  getBuoyDevices,
  getBuoyFilterDropdownOptions,
  getFirstAvailableBuoyField,
} from '../../Buoy/graph/buoyGraphUtils';
import { isBuoyDevice } from '../../Buoy/utils/buoyDeviceUtils';
import {
  GraphBuilderContext,
  GraphDeviceFamily,
  GraphFamilyRegistryEntry,
  GraphMetricOption,
} from '../types/GraphTypes';
import { getStageDevices } from '../utils/deviceGraph.utils';
import {
  getUSGSDevices,
  getUSGSParameterNamesForSelected,
  isUSGSDevice,
} from '../utils/usgsDeviceGraph.utils';
import { useBuoyGraphBuilder } from './BuoyGraphBuilder';
import { useFloodGraphBuilder } from './FloodGraphBuilder';
import { useUsgsGraphBuilder } from './UsgsGraphBuilder';

const floodMetricOptions: GraphMetricOption[] = [
  { value: 'sensordepths', label: 'Actual Depths' },
  { value: 'depths', label: 'Sensor Depths' },
  { value: 'temp', label: 'Temperature' },
  { value: 'baro', label: 'Barometric Pressure' },
  { value: 'battery', label: 'Battery' },
  { value: 'cell signal', label: 'Cell Signal' },
];

export const graphFamilyRegistry: GraphFamilyRegistryEntry[] = [
  {
    familyKey: 'Flood',
    label: 'Flood',
    useGraphBuilder: useFloodGraphBuilder,
    matchesDevice: (device) => {
      const candidate = device as Parameters<typeof DeviceIsSystemType>[0];
      return DeviceIsSystemType(candidate, 'Flood') && DeviceIsDeviceType(candidate, 'Stage');
    },
    getDeviceOptions: (devices) =>
      getStageDevices(devices).map((device) => ({
        label: device.alias,
        value: String(device.id),
        group: device.system_type,
      })),
    getMetricOptions: () => floodMetricOptions,
    getDefaultMetric: () => 'sensordepths',
  },
  {
    familyKey: 'USGS',
    label: 'USGS',
    useGraphBuilder: useUsgsGraphBuilder,
    matchesDevice: isUSGSDevice,
    getDeviceOptions: (devices) =>
      getUSGSDevices(devices).map((device) => ({
        label: device.alias || device.monitoring_location_name || device.name,
        value: String(device.id),
        group: device.system_type,
      })),
    getMetricOptions: ({ devices, selectedDeviceMap, unitType }) => {
      const params = getUSGSParameterNamesForSelected(devices, selectedDeviceMap, unitType) || [];
      if (!params.length) return [{ value: 'Gage height', label: 'Gage Height' }];

      return params.map((parameter) => {
        const name = parameter && typeof parameter === 'object' ? parameter.name : parameter;
        const unit = parameter && typeof parameter === 'object' ? parameter.unit : null;
        return {
          value: name,
          label: unit ? `${name} (${unit})` : name,
        };
      });
    },
    getDefaultMetric: () => 'Gage height',
  },
  {
    familyKey: 'Buoy',
    label: 'Buoy',
    useGraphBuilder: useBuoyGraphBuilder,
    matchesDevice: isBuoyDevice,
    getDeviceOptions: (devices) =>
      getBuoyDevices(devices).map((device) => ({
        label: device.alias || device.monitoring_location_name || device.name,
        value: String(device.id),
        group: 'Buoy',
      })),
    getMetricOptions: () =>
      getBuoyFilterDropdownOptions().map((option) => ({
        value: option.props.value,
        label: option.props.children,
      })),
    getDefaultMetric: (context) => getFirstAvailableBuoyField(context)?.value ?? 'wave_height',
  },
];

export const getGraphFamilyForDevice = (
  device: Record<string, unknown> | null | undefined,
): GraphFamilyRegistryEntry | null => {
  if (!device) return null;
  return graphFamilyRegistry.find((entry) => entry.matchesDevice(device)) ?? null;
};

export const getSelectedGraphFamily = (
  devices: Array<Record<string, unknown>> = [],
  selectedDeviceMap: Record<string, boolean> = {},
): GraphFamilyRegistryEntry | null => {
  const selectedDevice = devices.find((device) => selectedDeviceMap?.[String(device.id)]);
  return getGraphFamilyForDevice(selectedDevice);
};

export const getGraphDeviceIds = (devices: Array<Record<string, unknown>> = []) =>
  Array.from(
    new Set(
      graphFamilyRegistry.flatMap((entry) =>
        devices
          .filter(entry.matchesDevice)
          .map((device) => String(device.id))
          .filter(Boolean),
      ),
    ),
  );

export const getGraphDeviceOptions = (devices: Array<Record<string, unknown>> = []) =>
  graphFamilyRegistry.flatMap((entry) => entry.getDeviceOptions(devices));

export const getGraphMetricOptions = (
  family: GraphFamilyRegistryEntry | null,
  context: Pick<GraphBuilderContext, 'devices' | 'selectedDeviceMap' | 'unitType'>,
) => (family ?? graphFamilyRegistry[0]).getMetricOptions(context);

export const getDefaultGraphMetric = (
  family: GraphFamilyRegistryEntry | null,
  context: Pick<GraphBuilderContext, 'devices' | 'selectedDeviceMap' | 'unitType'> &
    Partial<Pick<GraphBuilderContext, 'startTime' | 'endTime'>>,
) => (family ?? graphFamilyRegistry[0]).getDefaultMetric(context);

export const isGraphOptionDisabled = (
  activeFamily: GraphDeviceFamily | null,
  option: { group?: string } | null | undefined,
) => {
  if (!activeFamily) return false;
  const group = option?.group ?? '';
  if (activeFamily === 'USGS') return group === 'Flood' || group === 'Buoy';
  if (activeFamily === 'Flood') return group !== 'Flood';
  if (activeFamily === 'Buoy') return group !== 'Buoy';
  return false;
};
